import { useState } from "react";

export enum ImportStepEnum {
  UPLOAD,
  MAPPING,
  VALIDATION,
  PROCESSING,
  COMPLETED,
}

export enum FieldMappingCiroFieldEnum {
  FIRST_NAME = "First name",
  LAST_NAME = "Last name",
  JOB_TITLE = "Job title",
  CONTACT_LINKEDIN_URL = "Contact LinkedIn URL",
  COMPANY_NAME = "Company name",
  COMPANY_WEBSITE_DOMAIN = "Company website domain",
  COMPANY_LINKEDIN_URL = "Company LinkedIn URL",
}

export interface IFieldMapping {
  csvField: string | null;
  ciroField: FieldMappingCiroFieldEnum;
  required: boolean;
}

export interface IContactInput {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  companyName?: string | null;
  companyWebsiteDomain?: string | null;
  companyLinkedinUrl?: string | null;
  contactLinkedinUrl?: string | null;
}

export interface IImportProgress {
  id: string;
  processedCount: number;
  totalCount: number;
  duplicateContacts: IContactInput[];
  updatedContacts: { id: number }[];
  completedContacts: { id: number }[];
  errors: string[];
}

export enum ContactInvalidReasonsEnum {
  MISSING_FIRST_NAME = "Missing first name",
  MISSING_LAST_NAME = "Missing last name",
  MISSING_COMPANY_NAME = "Missing company name",
  MISSING_CONTACT_LINKEDIN_OR_COMPANY = "Missing both contact LinkedIn URL and company name",
  INVALID_CONTACT_LINKEDIN_URL = "Invalid contact LinkedIn URL",
  INVALID_COMPANY_LINKEDIN_URL = "Invalid company LinkedIn URL",
  INVALID_COMPANY_DOMAIN = "Invalid company website domain",
  MISSING_REQUIRED_FIELDS = "Missing required fields",
  INVALID_DATA_FORMAT = "Invalid data format",
}

export interface IImportState {
  step: ImportStepEnum;
  setStep: (step: ImportStepEnum) => void;
  csvData: any[];
  setCsvData: (data: any[]) => void;
  csvHeaders: string[];
  setCsvHeaders: (headers: string[]) => void;
  mappings: IFieldMapping[];
  setMappings: (mappings: IFieldMapping[]) => void;
  error: string | null;
  setError: (error: string | null) => void;
  isProcessing: boolean;
  setIsProcessing: (isProcessing: boolean) => void;
  validatedContacts: {
    valid: any[];
    invalid: any[];
    invalidReasons: Record<number, ContactInvalidReasonsEnum[]>;
  };
  setValidatedContacts: (validatedContacts: {
    valid: any[];
    invalid: any[];
    invalidReasons: Record<number, ContactInvalidReasonsEnum[]>;
  }) => void;
  showOnlyInvalid: boolean;
  setShowOnlyInvalid: (showOnlyInvalid: boolean) => void;
  importProgress: IImportProgress;
  setImportProgress: (importProgress: IImportProgress) => void;
  showDuplicatesOnly: boolean;
  setShowDuplicatesOnly: (showDuplicatesOnly: boolean) => void;
  subscriptionEnabled: boolean;
  setSubscriptionEnabled: (enabled: boolean) => void;
  subscriptionVariables: {
    listId: number;
    contacts: IContactInput[];
  } | null;
  setSubscriptionVariables: (
    variables: {
      listId: number;
      contacts: IContactInput[];
    } | null,
  ) => void;
  reset: () => void;
}

export const useImportState = (): IImportState => {
  const [step, setStep] = useState<ImportStepEnum>(ImportStepEnum.UPLOAD);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<IFieldMapping[]>([
    {
      csvField: null,
      ciroField: FieldMappingCiroFieldEnum.FIRST_NAME,
      required: true,
    },
    {
      csvField: null,
      ciroField: FieldMappingCiroFieldEnum.LAST_NAME,
      required: true,
    },
    {
      csvField: null,
      ciroField: FieldMappingCiroFieldEnum.JOB_TITLE,
      required: false,
    },
    {
      csvField: null,
      ciroField: FieldMappingCiroFieldEnum.CONTACT_LINKEDIN_URL,
      required: false, // Not required by default, but either this or company name must be provided
    },
    {
      csvField: null,
      ciroField: FieldMappingCiroFieldEnum.COMPANY_NAME,
      required: false, // Not required by default, but either this or contact LinkedIn URL must be provided
    },
    {
      csvField: null,
      ciroField: FieldMappingCiroFieldEnum.COMPANY_WEBSITE_DOMAIN,
      required: false,
    },
    {
      csvField: null,
      ciroField: FieldMappingCiroFieldEnum.COMPANY_LINKEDIN_URL,
      required: false,
    },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validatedContacts, setValidatedContacts] = useState<{
    valid: any[];
    invalid: any[];
    invalidReasons: Record<number, ContactInvalidReasonsEnum[]>;
  }>({ valid: [], invalid: [], invalidReasons: {} });
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);
  const [importProgress, setImportProgress] = useState<IImportProgress>({
    id: crypto.randomUUID(),
    processedCount: 0,
    totalCount: 0,
    duplicateContacts: [],
    completedContacts: [],
    updatedContacts: [],
    errors: [],
  });
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [subscriptionVariables, setSubscriptionVariables] = useState<{
    listId: number;
    contacts: IContactInput[];
  } | null>(null);

  const reset = () => {
    setStep(ImportStepEnum.UPLOAD);
    setCsvData([]);
    setCsvHeaders([]);
    setMappings(mappings.map((m) => ({ ...m, csvField: null })));
    setError(null);
    setIsProcessing(false);
    setValidatedContacts({ valid: [], invalid: [], invalidReasons: {} });
    setShowOnlyInvalid(false);
    setImportProgress({
      id: crypto.randomUUID(),
      processedCount: 0,
      totalCount: 0,
      duplicateContacts: [],
      completedContacts: [],
      updatedContacts: [],
      errors: [],
    });
    setShowDuplicatesOnly(false);
    setSubscriptionEnabled(false);
    setSubscriptionVariables(null);
  };

  return {
    step,
    setStep,
    csvData,
    setCsvData,
    csvHeaders,
    setCsvHeaders,
    mappings,
    setMappings,
    error,
    setError,
    isProcessing,
    setIsProcessing,
    validatedContacts,
    setValidatedContacts,
    showOnlyInvalid,
    setShowOnlyInvalid,
    importProgress,
    setImportProgress,
    showDuplicatesOnly,
    setShowDuplicatesOnly,
    subscriptionEnabled,
    setSubscriptionEnabled,
    subscriptionVariables,
    setSubscriptionVariables,
    reset,
  };
};
